import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X } from 'lucide-react';

interface RulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RulesModal({ isOpen, onClose }: RulesModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-xl bg-card doodle-border p-5 md:p-12 shadow-doodle-xl overflow-hidden"
          >
            <button 
              onClick={onClose}
              className="absolute top-3 md:top-4 right-3 md:right-4 p-2 hover:bg-slate-100 rounded-full transition-colors boing"
            >
              <X className="w-5 h-5 md:w-6 md:h-6 text-slate-400" />
            </button>

            <div className="space-y-4 md:space-y-8">
              <div className="text-center space-y-1.5 md:space-y-2">
                <div className="inline-flex items-center gap-1.5 md:gap-2 bg-sky-100 text-sky-600 px-3 md:px-4 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest handwritten">
                  <BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  SI TË LUASH
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-foreground handwritten uppercase">RREGULLAT E LOJËS</h2>
              </div>

              <div className="space-y-3 md:space-y-6 max-h-[50vh] md:max-h-[60vh] overflow-y-auto no-scrollbar px-1 md:px-4 py-2 md:py-4 pr-1 md:pr-2">
                <div className="bg-amber-50 p-4 md:p-6 doodle-border-soft border-amber-200 rotate-1 shadow-doodle-sm">
                  <h3 className="font-black text-lg md:text-xl text-amber-700 mb-2 md:mb-3 handwritten">➜ Logjika e Fjalëve</h3>
                  <ul className="space-y-1 md:space-y-2 text-foreground font-bold handwritten text-sm md:text-lg">
                    <li>• Çdo fjalë duhet të fillojë me shkronjën e fundit të fjalës paraardhëse.</li>
                    <li>• Fjalët duhet të jenë të paktën 2 shkronja të gjata.</li>
                    <li>• Nuk lejohet përsëritja e fjalëve të përdorura më parë.</li>
                  </ul>
                </div>
                
                <div className="bg-rose-50 p-4 md:p-6 doodle-border-soft border-rose-200 -rotate-1 shadow-doodle-sm">
                  <h3 className="font-black text-lg md:text-xl text-rose-700 mb-2 md:mb-3 handwritten">➜ Koha dhe Pikët</h3>
                  <ul className="space-y-1 md:space-y-2 text-foreground font-bold handwritten text-sm md:text-lg">
                    <li>• Loja fillon me <span className="text-rose-500 underline decoration-2">15 sekonda</span> për çdo radhë.</li>
                    <li>• <span className="text-emerald-600">+2 sekonda</span> bonus (deri në 30s) nëse je i shpejtë!</li>
                    <li>• Nëse koha bie nën 10s, një fjalë e saktë të kthen në 15s.</li>
                    <li>• <span className="text-rose-600">-2 sekonda</span> ndëshkim nëse shkruan fjalë të gabuara.</li>
                  </ul>
                </div>

                <div className="bg-sky-50 p-4 md:p-6 doodle-border-soft border-sky-200 rotate-1 shadow-doodle-sm">
                  <h3 className="font-black text-lg md:text-xl text-sky-700 mb-2 md:mb-3 handwritten">➜ Drejtshkrimi & Pikët</h3>
                  <ul className="space-y-1 md:space-y-2 text-foreground font-bold handwritten text-sm md:text-lg">
                    <li>• Zëvendësimi i <span className="text-sky-600">ë ➔ e</span> dhe <span className="text-sky-600">ç ➔ c</span> lejohet për shpejtësi (pikë të plota).</li>
                    <li>• Harresa e <span className="text-amber-600">-ë</span> në fund të fjalës vlerësohet me <span className="text-amber-600">50% të pikëve</span>.</li>
                    <li>• Ngatërrimi i <span className="text-amber-600">q ➔ ç</span> ose <span className="text-amber-600">gj ➔ xh</span> vlerësohet me <span className="text-amber-600">50% të pikëve</span>.</li>
                    <li>• Loja do t&apos;ju tregojë gjithmonë formën e saktë të fjalës!</li>
                  </ul>
                </div>

                <div className="bg-muted p-4 md:p-6 doodle-border-soft border-slate-200 -rotate-1 shadow-doodle-sm">
                  <h3 className="font-black text-lg md:text-xl text-slate-700 mb-2 md:mb-3 handwritten">➜ Këshillë</h3>
                  <p className="text-foreground font-bold handwritten text-sm md:text-lg italic">&quot;Kliko mbi fjalët në historik për të parë kuptimin e tyre nga fjalori!&quot;</p>
                </div>
              </div>

              <button 
                onClick={onClose}
                className="w-full bg-emerald-500 text-white py-3.5 md:py-5 doodle-border shadow-doodle-sm md:shadow-doodle font-black text-lg md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
              >
                E KUPTOVA!
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
